import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  collectSignatureFileTargets,
  type FormFillSession,
  type FormMediaContext,
} from '@elva/ui';
import { keys } from '@/api/queryKeys';
import { resolveStaffSubmissionFileUrl } from '../api/formMediaApi';

function collectBlobUrls(media: FormMediaContext | undefined): string[] {
  if (!media) return [];
  return [
    ...Object.values(media.fileUrlsByKey ?? {}),
    ...Object.values(media.fileUrlsByBlockId ?? {}),
    ...Object.values(media.signatureUrlsByBlockId ?? {}),
  ].filter((url) => url.startsWith('blob:'));
}

export function useResolvedFormMediaUrls(
  session: FormFillSession | null | undefined,
  accessToken?: string,
  tokenReady = true,
) {
  const submissionId = session?.submission.id ?? session?.submission.submission_id ?? '';
  const assignmentId = session?.assignment.id || session?.submission.assignment_id || '';
  const targets = session ? collectSignatureFileTargets(session) : [];
  const targetKey = targets.map((target) => `${target.mediaKey}:${target.fileKey}`).join('|');

  const query = useQuery({
    queryKey: keys.forms.submissionFile(submissionId, `${targetKey}:${accessToken ?? ''}`),
    queryFn: async ({ signal }): Promise<FormMediaContext> => {
      const fileUrlsByKey: Record<string, string> = {};
      const fileUrlsByBlockId: Record<string, string> = {};
      const signatureUrlsByBlockId: Record<string, string> = {};

      await Promise.all(
        targets.map(async (target) => {
          const url = await resolveStaffSubmissionFileUrl({
            fileKey: target.fileKey,
            fieldKey: target.mediaKey,
            ...(accessToken ? { accessToken } : {}),
            ...(submissionId ? { submissionId } : {}),
            ...(assignmentId ? { assignmentId } : {}),
            signal,
          });
          if (!url) return;
          fileUrlsByKey[target.fileKey] = url;
          fileUrlsByBlockId[target.mediaKey] = url;
          signatureUrlsByBlockId[target.loc.blockId] = url;
        }),
      );

      return { fileUrlsByKey, fileUrlsByBlockId, signatureUrlsByBlockId };
    },
    enabled: Boolean(session && targets.length > 0 && tokenReady),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    const media = query.data;
    return () => {
      for (const url of collectBlobUrls(media)) {
        URL.revokeObjectURL(url);
      }
    };
  }, [query.data]);

  return query;
}
